import React, { useState } from 'react'
import './HapticControls.css'

const HapticControls = ({
  // Mode Joy-Con (Bluetooth direct)
  joyConState,
  onJoyConConnect,
  onJoyConEnabledChange,
  onJoyConIntensityChange,
  // Mode Gamepad (8BitDo USB)
  gamepadState,
  onGamepadEnabledChange,
  onGamepadIntensityChange,
  onGamepadAssignSide,
  onGamepadScan,
  onGamepadTestVibration,
  // État partagé
  vibrationMode,
  onVibrationModeChange
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  // Utiliser les props si disponibles, sinon état local (pour rétrocompatibilité/test)
  const [localMode, setLocalMode] = useState('joycon')
  
  const currentMode = vibrationMode || localMode
  const handleModeChange = (mode) => {
    if (onVibrationModeChange) {
      onVibrationModeChange(mode)
    } else {
      setLocalMode(mode)
    }
  }

  // Gestion de l'ouverture/fermeture = activation/désactivation
  const handleToggleExpanded = () => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)
    // Activer/désactiver la vibration en même temps
    handleToggleEnabled(newExpanded)
  }

  const isVibrationEnabled = currentMode === 'joycon' 
    ? joyConState?.enabled 
    : gamepadState?.enabled

  const isConnected = currentMode === 'joycon'
    ? joyConState?.connected
    : gamepadState?.connected

  const handleToggleEnabled = (enabled) => {
    if (currentMode === 'joycon') {
      onJoyConEnabledChange?.(enabled)
    } else {
      onGamepadEnabledChange?.(enabled)
    }
  }

  const handleIntensityChange = (intensity) => {
    if (currentMode === 'joycon') {
      onJoyConIntensityChange?.(intensity)
    } else {
      onGamepadIntensityChange?.(intensity)
    }
  }

  const currentIntensity = currentMode === 'joycon'
    ? joyConState?.intensity || 0.6
    : gamepadState?.intensity || 0.6

  return (
    <div className="haptic-section">
      {/* Header cliquable pour déplier/replier ET activer/désactiver */}
      <div 
        className="haptic-header"
        onClick={handleToggleExpanded}
      >
        <div className="haptic-header-left">
          <span className={`haptic-indicator ${isVibrationEnabled && isConnected ? 'active' : ''}`}>
            {isVibrationEnabled && isConnected ? '🟢' : '⚪'}
          </span>
          <span className="haptic-title">🎮 Vibration haptique</span>
        </div>
        <div className="haptic-header-right">
          <span className={`haptic-status ${isConnected ? 'connected' : ''}`}>
            {gamepadState?.gamepads?.length > 0 ? gamepadState.status : (isConnected ? 'Connecté' : 'Appuyez sur un bouton')}
          </span>
          <span className={`haptic-chevron ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {/* Contenu dépliable */}
      {isExpanded && (
        <div className="haptic-content">
          {/* Sélecteur de mode */}
          <div className="haptic-mode-selector">
            <button 
              className={`mode-btn ${currentMode === 'joycon' ? 'active' : ''}`}
              onClick={() => handleModeChange('joycon')}
            >
              <span className="mode-icon">🎮</span>
              <span className="mode-label">Joy-Con Bluetooth</span>
              <span className="mode-desc">Connexion directe (Mac)</span>
            </button>
            <button 
              className={`mode-btn ${currentMode === 'gamepad' ? 'active' : ''}`}
              onClick={() => handleModeChange('gamepad')}
            >
              <span className="mode-icon">🔌</span>
              <span className="mode-label">8BitDo USB</span>
              <span className="mode-desc">Via adaptateur (PC)</span>
            </button>
          </div>

          {/* Contenu selon le mode */}
          {currentMode === 'joycon' ? (
            <div className="mode-content">
              <p className="mode-info">
                Connectez vos Joy-Con en Bluetooth à votre ordinateur, puis cliquez sur le bouton ci-dessous.
              </p>
              
              <button 
                className="connect-btn"
                onClick={onJoyConConnect}
                disabled={!navigator.hid}
              >
                {joyConState?.connected ? '🔄 Reconnecter' : '🔗 Connecter Joy-Con'}
              </button>

              {!navigator.hid && (
                <p className="warning-text">
                  ⚠️ WebHID non supporté. Utilisez Chrome ou Edge.
                </p>
              )}

              {joyConState?.connected && (
                <div className="status-box success">
                  {joyConState.status}
                </div>
              )}
            </div>
          ) : (
            <div className="mode-content">
              <p className="mode-info">
                Branchez vos 8BitDo en USB et appuyez sur un bouton de chaque manette pour les détecter
              </p>

              {gamepadState?.gamepads?.length > 0 && (
                <div className="gamepads-list">
                  {gamepadState.gamepads.map((gp, index) => (
                    <div key={gp.index} className="gamepad-item">
                      <span className="gamepad-name">
                        {gp.hasVibration ? '✅' : '⚠️'} {gp.id.substring(0, 25)}...
                      </span>
                      <div className="gamepad-side-selector">
                        <button 
                          className={`side-btn ${gp.side === 'left' ? 'active' : ''}`}
                          onClick={() => onGamepadAssignSide?.(gp.index, 'left')}
                        >
                          ← Gauche
                        </button>
                        <button 
                          className={`side-btn ${gp.side === 'right' ? 'active' : ''}`}
                          onClick={() => onGamepadAssignSide?.(gp.index, 'right')}
                        >
                          Droite →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default HapticControls
